// File: components/PropertyPanels/IVRNodeProperties.jsx
import { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Select as ChakraSelect,
  Button,
  IconButton,
  FormLabel,
  FormControl,
  Switch,
  Divider,
  SimpleGrid,
  Flex,
  Icon,
  Alert,
  AlertIcon,
  AlertDescription,
  Center,
  Tag,
  Code,
} from '@chakra-ui/react';
import {
  FaSave,
  FaBolt,
} from 'react-icons/fa';
import { LuBraces, LuPhone, LuDatabase } from 'react-icons/lu';
import Select from 'react-select';
import VariablePickerDrawer from '../VariablePickerDrawer';
import API from '../../../helpers/axiosInstance';
import { getApiHeaders } from '../../../utils/DefaultFunction';

// Channel ID for Voice (IVR)
const IVR_CHANNEL_ID = 4;

const IVRNodeProperties = ({ nodeConfig, setNodeConfig, updateNodeData, nodes }) => {
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variablePickerOpen, setVariablePickerOpen] = useState(false);
  const [activeVariableKey, setActiveVariableKey] = useState(null);

  // ── Fetch IVR templates on mount ──────────────────────────────────────────
  useEffect(() => {
    fetchTemplates();
  }, []);

  // ── Pre-select template from existing nodeConfig ──────────────────────────
  useEffect(() => {
    if (nodeConfig.templateId && templates.length > 0) {
      const tpl = templates.find((t) => t.id === parseInt(nodeConfig.templateId));
      if (tpl && (!selectedTemplate || selectedTemplate.id !== tpl.id)) {
        setSelectedTemplate(tpl);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.length, nodeConfig.templateId]);

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await API.get(
        `${process.env.REACT_APP_API_URL}/template/msg-temp-type-ids`,
        {
          params: { channel_id: IVR_CHANNEL_ID },
          headers: getApiHeaders(),
        }
      );
      if (response.data?.data) {
        setTemplates(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching IVR templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // ── Parse ivr_variables from message_combine ──────────────────────────────
  const getIvrVariables = (template) => {
    if (!template) return [];
    try {
      const details = JSON.parse(template.message_combine || '{}');
      return Array.isArray(details.ivr_variables) ? details.ivr_variables : [];
    } catch {
      return [];
    }
  };

  // ── Template selection handler ────────────────────────────────────────────
  const handleTemplateSelect = (selectedOption) => {
    if (!selectedOption) {
      setSelectedTemplate(null);
      setNodeConfig({
        ...nodeConfig,
        templateId: null,
        templateName: null,
        voiceScript: null,
        ivrVariableMappings: {},
      });
      return;
    }

    const tpl = templates.find((t) => t.id === selectedOption.value);
    if (!tpl) return;
    setSelectedTemplate(tpl);

    let details = {};
    try {
      details = JSON.parse(tpl.message_combine || '{}');
    } catch (e) {
      console.error('Error parsing message_combine:', e);
    }

    const ivrVars = Array.isArray(details.ivr_variables) ? details.ivr_variables : [];

    // Preserve existing mappings for vars that still exist
    const currentMappings = nodeConfig.ivrVariableMappings || {};
    const newMappings = {};
    ivrVars.forEach((varName) => {
      newMappings[varName] = currentMappings[varName] || {
        sourceType: 'fixed',
        sourceValue: '',
        eventName: '',
        propertyName: '',
        fallbackValue: '',
      };
    });

    setNodeConfig({
      ...nodeConfig,
      templateId: tpl.id,
      templateName: tpl.name,
      voiceScript: details.voiceScript || '',
      ivrVariableMappings: newMappings,
    });
  };

  // ── Variable mapping helpers ──────────────────────────────────────────────
  // Uses functional setState so multiple calls in the same tick don't clobber each other.
  const updateMapping = (varName, updates) => {
    setNodeConfig((prev) => {
      const mappings = { ...(prev.ivrVariableMappings || {}) };
      mappings[varName] = { ...mappings[varName], ...updates };
      return { ...prev, ivrVariableMappings: mappings };
    });
  };

  const openVariablePicker = (varName) => {
    setActiveVariableKey(varName);
    setVariablePickerOpen(true);
  };

  const handleVariablePickerSelect = (varPath) => {
    if (!activeVariableKey) return;
    updateMapping(activeVariableKey, { sourceValue: `{{${varPath}}}` });
  };

  const isDynamic = (val) => val?.includes('{{') && val?.includes('}}');

  const ivrVariables = getIvrVariables(selectedTemplate);

  const templateOptions = templates.map((t) => ({
    value: t.id,
    label: t.name,
  }));

  const selectedTemplateValue = nodeConfig.templateId
    ? templateOptions.find((o) => o.value === parseInt(nodeConfig.templateId)) || null
    : null;

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '32px',
      height: '32px',
      fontSize: '13px',
      borderColor: state.isFocused ? 'var(--brand-primary)' : '#E2E8F0',
      boxShadow: state.isFocused ? '0 0 0 1px var(--brand-primary)' : 'none',
      '&:hover': { borderColor: '#CBD5E0' },
    }),
    valueContainer: (provided) => ({ ...provided, height: '32px', padding: '0 8px' }),
    input: (provided) => ({ ...provided, margin: '0', padding: '0' }),
    indicatorsContainer: (provided) => ({ ...provided, height: '32px' }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '13px',
      backgroundColor: state.isSelected ? 'var(--brand-primary)' : state.isFocused ? 'var(--brand-tint)' : 'white',
      color: state.isSelected ? 'white' : '#2D3748',
    }),
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
  };

  return (
    <VStack spacing={4} align="stretch">

      {/* ── Step Name ──────────────────────────────────────────────────────── */}
      <Box>
        <FormLabel fontSize="sm">Step Name</FormLabel>
        <Input
          value={nodeConfig.label || ''}
          onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
          placeholder="e.g., IVR Call"
          size="sm"
        />
      </Box>

      <Divider />

      {/* ── Template Selection ─────────────────────────────────────────────── */}
      <FormControl>
        <FormLabel fontSize="sm">
          <HStack spacing={1}>
            <Icon as={LuPhone} boxSize={3.5} color="orange.500" />
            <Text>IVR Template</Text>
          </HStack>
        </FormLabel>
        <Select
          value={selectedTemplateValue}
          onChange={handleTemplateSelect}
          options={templateOptions}
          placeholder={loadingTemplates ? 'Loading templates…' : 'Select IVR template'}
          isDisabled={loadingTemplates}
          isLoading={loadingTemplates}
          isClearable
          isSearchable
          styles={customSelectStyles}
          noOptionsMessage={() => 'No IVR templates found'}
        />
      </FormControl>

      {/* ── Voice Script preview ──────────────────────────────────────────── */}
      {selectedTemplate && nodeConfig.voiceScript && (
        <Box p={3} bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md">
          <Text fontSize="xs" fontWeight="semibold" color="orange.700" mb={1}>
            Voice Script
          </Text>
          <Text fontSize="xs" color="orange.800" fontFamily="mono">
            {nodeConfig.voiceScript}
          </Text>
        </Box>
      )}

      {/* ── IVR Variables ─────────────────────────────────────────────────── */}
      {selectedTemplate && ivrVariables.length > 0 && (
        <>
          <Divider />
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={0.5}>
              IVR Variables
            </Text>
            <Text fontSize="xs" color="gray.500" mb={3}>
              Map each variable to a fixed value or a flow context variable
            </Text>

            <VStack spacing={2} align="stretch">
              {ivrVariables.map((varName) => {
                const mapping = (nodeConfig.ivrVariableMappings || {})[varName] || {
                  sourceType: 'fixed',
                  sourceValue: '',
                  eventName: '',
                  propertyName: '',
                  fallbackValue: '',
                };

                return (
                  <Box
                    key={varName}
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    p={3}
                  >
                    <HStack spacing={2} mb={2}>
                      <Icon as={LuDatabase} boxSize={3.5} color="var(--brand-primary)" />
                      <Code fontSize="xs" colorScheme="orange">
                        {varName}
                      </Code>
                    </HStack>

                    {/* Source type select */}
                    <ChakraSelect
                      value={mapping.sourceType}
                      onChange={(e) => updateMapping(varName, { sourceType: e.target.value, sourceValue: '' })}
                      size="sm"
                      fontSize="xs"
                      mb={2}
                    >
                      <option value="fixed">Fixed Value</option>
                      <option value="flow_context">Flow Variable</option>
                    </ChakraSelect>

                    {mapping.sourceType === 'fixed' ? (
                      <Input
                        placeholder={`Enter value for ${varName}`}
                        value={mapping.sourceValue}
                        onChange={(e) => updateMapping(varName, { sourceValue: e.target.value })}
                        size="sm"
                        fontSize="xs"
                      />
                    ) : (
                      <Box position="relative">
                        <Input
                          placeholder="Click {x} to pick a flow variable"
                          value={mapping.sourceValue}
                          onChange={(e) => updateMapping(varName, { sourceValue: e.target.value })}
                          size="sm"
                          fontSize="xs"
                          fontFamily="mono"
                          pr="36px"
                          bg={isDynamic(mapping.sourceValue) ? 'blue.50' : undefined}
                          borderColor={isDynamic(mapping.sourceValue) ? 'blue.200' : undefined}
                        />
                        <IconButton
                          aria-label="Pick flow variable"
                          icon={<LuBraces />}
                          size="xs"
                          variant="ghost"
                          color="var(--brand-primary)"
                          _hover={{ bg: 'blue.50' }}
                          position="absolute"
                          right={1}
                          top="50%"
                          transform="translateY(-50%)"
                          zIndex={1}
                          onClick={() => openVariablePicker(varName)}
                        />
                      </Box>
                    )}

                    {isDynamic(mapping.sourceValue) && (
                      <Tag size="sm" variant="outline" bg="blue.50" color="var(--brand-primary)" borderColor="blue.200" mt={1.5}>
                        Dynamic Variable
                      </Tag>
                    )}
                  </Box>
                );
              })}
            </VStack>
          </Box>
        </>
      )}

      {/* Empty state when template selected but no ivr_variables */}
      {selectedTemplate && ivrVariables.length === 0 && (
        <Center py={3} border="2px dashed" borderColor="gray.200" borderRadius="md">
          <Text fontSize="xs" color="gray.400">
            This template has no IVR variables defined
          </Text>
        </Center>
      )}

      <Divider />

      {/* ── Enable Retries ─────────────────────────────────────────────────── */}
      <Box>
        <Flex justify="space-between" align="center" mb={3}>
          <Box>
            <Text fontSize="sm" fontWeight="medium">Enable Retries</Text>
            <Text fontSize="xs" color="gray.600" mt={0.5}>
              Automatically retry if call fails
            </Text>
          </Box>
          <Switch
            isChecked={nodeConfig.enableRetries || false}
            onChange={(e) =>
              setNodeConfig({ ...nodeConfig, enableRetries: e.target.checked })
            }
          />
        </Flex>

        {nodeConfig.enableRetries && (
          <Box p={3} border="1px solid" borderColor="blue.200" borderRadius="lg" bg="blue.50">
            <Flex align="center" gap={2} fontSize="xs" color="blue.700" mb={3}>
              <Icon as={FaBolt} />
              <Text fontWeight="medium">Retry Configuration</Text>
            </Flex>

            <VStack spacing={3} align="stretch">
              <Box>
                <FormLabel fontSize="xs">Number of Retries</FormLabel>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={nodeConfig.noOfRetries || 3}
                  onChange={(e) =>
                    setNodeConfig({ ...nodeConfig, noOfRetries: Number.parseInt(e.target.value) || 1 })
                  }
                  size="sm"
                />
              </Box>

              <SimpleGrid columns={2} spacing={2}>
                <Box>
                  <FormLabel fontSize="xs">Wait Time</FormLabel>
                  <Input
                    type="number"
                    min={1}
                    value={nodeConfig.waitTimeBetweenRetries || 5}
                    onChange={(e) =>
                      setNodeConfig({ ...nodeConfig, waitTimeBetweenRetries: Number.parseInt(e.target.value) || 1 })
                    }
                    size="sm"
                  />
                </Box>
                <Box>
                  <FormLabel fontSize="xs">Unit</FormLabel>
                  <ChakraSelect
                    value={nodeConfig.waitTimeUnit || 'minutes'}
                    onChange={(e) =>
                      setNodeConfig({ ...nodeConfig, waitTimeUnit: e.target.value })
                    }
                    size="sm"
                  >
                    <option value="seconds">Seconds</option>
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                  </ChakraSelect>
                </Box>
              </SimpleGrid>

              <Box fontSize="xs" color="blue.700" bg="blue.100" px={2} py={1.5} borderRadius="md">
                <Text as="span" fontWeight="semibold">Note: </Text>
                The system will retry the call {nodeConfig.noOfRetries || 3} time
                {(nodeConfig.noOfRetries || 3) > 1 ? 's' : ''}, waiting{' '}
                {nodeConfig.waitTimeBetweenRetries || 5}{' '}
                {nodeConfig.waitTimeUnit || 'minutes'} between each attempt.
              </Box>
            </VStack>
          </Box>
        )}
      </Box>

      <Divider />

      {/* ── Info Alert ─────────────────────────────────────────────────────── */}
      <Alert status="info" fontSize="xs" borderRadius="md" border="1px solid #fee685" bg="#fffbeb" color="#973c00">
        <AlertIcon boxSize={4} color="#973c00" />
        <AlertDescription>
          <Text fontWeight="semibold" mb={1}>How IVR Calls Work</Text>
          <Text>
            The system will initiate an IVR call to the customer.{' '}
            {nodeConfig.enableRetries
              ? `If the call fails, it will automatically retry ${nodeConfig.noOfRetries || 3} times with ${nodeConfig.waitTimeBetweenRetries || 5} ${nodeConfig.waitTimeUnit || 'minutes'} between attempts.`
              : 'If the call fails, the flow will continue to the next node.'}
          </Text>
        </AlertDescription>
      </Alert>

      {/* ── Save ───────────────────────────────────────────────────────────── */}
      <Button
        colorScheme="blue"
        onClick={updateNodeData}
        leftIcon={<Icon as={FaSave} />}
        bg="#155dfc"
        _hover={{ bg: 'var(--brand-primary-hover)' }}
        isDisabled={!selectedTemplate}
      >
        Update Node
      </Button>

      {/* ── Variable Picker Drawer ─────────────────────────────────────────── */}
      <VariablePickerDrawer
        isOpen={variablePickerOpen}
        onClose={() => setVariablePickerOpen(false)}
        onSelect={handleVariablePickerSelect}
        flowNodes={nodes}
      />
    </VStack>
  );
};

export default IVRNodeProperties;
